import React from 'react'
// libs
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { isFirefox, isMobileSafari, isSafari } from 'react-device-detect'
// hooks
import { useMicrosoftLogin } from 'hooks/useMicrosoftLogin'
// utils
import { getLastAuthMethod, isManualLogout, markSsoRedirectTried, wasSsoRedirectTried } from 'utils/authStorage'

// Safari (ITP) and Firefox (Total Cookie Protection) block the hidden iframe that
// MSAL's ssoSilent relies on — those browsers use the full-page redirect instead.
const BLOCKS_THIRD_PARTY_COOKIES = isSafari || isMobileSafari || isFirefox

/**
 * Drives the no-click SSO attempt on the login page and exposes what the form
 * needs. `microsoftSignIn` is returned from here so the login button shares this
 * hook's `useMicrosoftLogin` instance (same `completingRef` / `silentAbortedRef`).
 */
export const useAutoSso = () => {
  const { inProgress } = useMsal()
  const { microsoftSignIn, trySilentSignIn, abortSilentSignIn, isCompleting } = useMicrosoftLogin()

  // Kept behind a loader so the form doesn't flash for users signed in silently.
  const [resolvingSso, setResolvingSso] = React.useState(true)

  const attemptedRef = React.useRef(false)

  React.useEffect(() => {
    if (attemptedRef.current || inProgress !== InteractionStatus.None) {
      return
    }

    attemptedRef.current = true

    // Skip while the user is manually logged out (sticky until they click "Login
    // with Microsoft"), or when they last signed in with a password.
    if (isManualLogout() || getLastAuthMethod() === 'password') {
      setResolvingSso(false)
      return
    }

    // Safari / Firefox can't run ssoSilent at all (see above).
    if (BLOCKS_THIRD_PARTY_COOKIES) {
      // Known Microsoft user → full-page redirect instead — once per tab session,
      // so a cancelled redirect doesn't trap them on this screen.
      if (getLastAuthMethod() === 'microsoft') {
        if (wasSsoRedirectTried()) {
          // Back from the redirect: keep the loader if the login is still being
          // completed (success), otherwise it was cancelled/failed — show the form.
          if (!isCompleting()) {
            setResolvingSso(false)
          }
          return
        }

        markSsoRedirectTried()
        microsoftSignIn()
        return
      }

      // Everyone else — the hidden iframe would only stall, show the form now.
      setResolvingSso(false)
      return
    }

    trySilentSignIn().then((signedIn) => {
      // On success completeLogin() has already navigated away — keep the loader.
      if (!signedIn) {
        setResolvingSso(false)
      }
    })
  }, [inProgress])

  const skipSilentSso = () => {
    abortSilentSignIn()
    setResolvingSso(false)
  }

  return { resolvingSso, skipSilentSso, microsoftSignIn }
}
